// HELPERS (founder 2026-10-30, helpers become a revenue stream): "Ask for a helper". A person says
// what a DE should be able to do; that files a helper request (`agent_request`, kind `helper`, for
// that DE) in the operator's queue. It is paid: Requested → Quote ready (the operator, or the
// billing side on wp/SKILLSMITH-OPPORTUNITIES, sends a price) → Approved (the client, here) →
// Paid → Building → Added, or Declined at any step. The client sees each stage on the DE's
// Helpers tab and approves a quote there. A small action, so a modal.
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Textarea,
  toast,
  workerName,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SparklesIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { isManager } from "../company";
import { problemText } from "../de/de-status";
import { formatDate } from "../format";
import { agentRequestKeys } from "./agent-requests";
import type { Helper } from "./helpers";

type RequestRow = Awaited<ReturnType<Api["client"]["agentRequests"]["list"]>>["data"][number];
export type HelperStage = NonNullable<RequestRow["stage"]>;
type Quote = NonNullable<RequestRow["quote"]>;

/** What the client reads for each stage. */
export const HELPER_STAGE: Record<
  HelperStage,
  { label: string; variant: "neutral" | "warning" | "info" | "success" }
> = {
  requested: { label: "Requested", variant: "neutral" },
  quoted: { label: "Quote ready", variant: "warning" },
  approved: { label: "Approved", variant: "info" },
  paid: { label: "Paid", variant: "info" },
  building: { label: "Building", variant: "info" },
  added: { label: "Added", variant: "success" },
  declined: { label: "Declined", variant: "neutral" },
};

/** "$99.00 / month", "€250.00 once". */
export function quoteText(q: Pick<Quote, "amount_minor" | "currency" | "interval">): string {
  let money: string;
  try {
    money = new Intl.NumberFormat(undefined, { style: "currency", currency: q.currency }).format(
      q.amount_minor / 100,
    );
  } catch {
    money = `${(q.amount_minor / 100).toFixed(2)} ${q.currency}`;
  }
  return q.interval === "month" ? `${money} / month` : `${money} once`;
}

/** A DE's helper requests (the list key's child, so any request change refreshes it). */
export const helperRequestsQuery = (api: Api, co: string, de: string) =>
  queryOptions({
    queryKey: [...agentRequestKeys.list(co), "helper", de] as const,
    queryFn: () =>
      api.client.agentRequests.list({
        params: { co },
        query: { kind: "helper", de_membership_id: de },
      }),
  });

/** Who may ask: a person of the company, not only a client viewer (the API's rule). */
export const mayAskForHelper = (company: CompanyAccess, meId: string | null) =>
  meId !== null && company.roles.some((r) => r !== "client_viewer");

/** "Ask for a helper": a labelled button or a hover icon; the modal files the request. */
export function AskForHelperButton({
  api,
  company,
  meId,
  de,
  des,
  available = [],
  variant = "labelled",
}: {
  api: Api;
  company: CompanyAccess;
  meId: string | null;
  /** The DE it is for; without one the modal asks which (from `des`). */
  de?: { membership_id: string; display_name: string } | undefined;
  des?: readonly { membership_id: string; display_name: string }[] | undefined;
  /** Not-deployed helpers to start from (chips that fill the form). */
  available?: readonly Helper[];
  variant?: "labelled" | "link" | "icon";
}) {
  const [open, setOpen] = useState(false);
  if (!mayAskForHelper(company, meId)) return null;
  if (!de && !des?.length) return null;
  const label = "Ask for a helper";
  return (
    <>
      {variant === "icon" ? (
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label={label}
          title={label}
          data-slot="ask-for-helper"
          onClick={() => setOpen(true)}
        >
          <SparklesIcon aria-hidden="true" />
        </Button>
      ) : variant === "link" ? (
        <button
          type="button"
          data-slot="ask-for-helper"
          className="text-xs text-primary-text underline-offset-4 hover:underline"
          onClick={() => setOpen(true)}
        >
          {label}
        </button>
      ) : (
        <Button
          size="sm"
          variant="secondary"
          data-slot="ask-for-helper"
          onClick={() => setOpen(true)}
        >
          <SparklesIcon aria-hidden="true" />
          {label}
        </Button>
      )}
      {open ? (
        <AskForHelperDialog
          api={api}
          company={company}
          de={de}
          des={des ?? []}
          available={available}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

function AskForHelperDialog({
  api,
  company,
  de,
  des,
  available,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  de: { membership_id: string; display_name: string } | undefined;
  des: readonly { membership_id: string; display_name: string }[];
  available: readonly Helper[];
  onClose: () => void;
}) {
  const id = useId();
  const qc = useQueryClient();
  const [deId, setDeId] = useState(de?.membership_id ?? des[0]?.membership_id ?? "");
  const [what, setWhat] = useState("");
  const [name, setName] = useState("");
  const forName = workerName(
    de?.display_name ?? des.find((d) => d.membership_id === deId)?.display_name ?? "",
  );
  const send = useMutation({
    mutationFn: () => {
      const text = what.trim();
      const role = (name.trim() || text.split("\n")[0] || text).slice(0, 200);
      return api.client.agentRequests.create({
        params: { co: company.id },
        body: { kind: "helper", role, note: text, de_membership_id: deId },
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: agentRequestKeys.list(company.id) });
      toast.add({ title: "Helper requested", type: "success" });
      onClose();
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "The request was not sent."), type: "error" }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!what.trim() || !deId) return;
    send.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-slot="ask-for-helper-dialog">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-1">
              Ask for a helper
              <InfoTip label="About helper requests">
                Say what {forName || "the DE"} should be able to do. We send a quote; once you
                approve it, we build the helper and add it. You see each step here.
              </InfoTip>
            </DialogTitle>
            <DialogDescription>{forName ? `For ${forName}` : "For a DE"}</DialogDescription>
          </DialogHeader>
          {!de ? (
            <Field>
              <FieldLabel htmlFor={`${id}-de`}>For</FieldLabel>
              <NativeSelect id={`${id}-de`} value={deId} onChange={(e) => setDeId(e.target.value)}>
                {des.map((d) => (
                  <NativeSelectOption key={d.membership_id} value={d.membership_id}>
                    {workerName(d.display_name)}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
          ) : null}
          {available.length ? (
            <div className="flex flex-wrap items-center gap-1" data-slot="ask-available">
              <span className="text-xs text-muted-foreground">Start from</span>
              {available.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="rounded-full border border-subtle px-2 text-xs leading-5 text-muted-foreground hover:border-strong hover:text-foreground"
                  onClick={() => {
                    setName(workerName(a.name));
                    if (!what.trim() && a.role) setWhat(a.role.slice(0, 2000));
                  }}
                >
                  {workerName(a.name)}
                </button>
              ))}
            </div>
          ) : null}
          <Field>
            <FieldLabel htmlFor={`${id}-what`}>What should it do?</FieldLabel>
            <Textarea
              id={`${id}-what`}
              rows={4}
              maxLength={2000}
              value={what}
              placeholder="Check every draft against our brand voice before it goes out"
              onChange={(e) => setWhat(e.target.value)}
              autoFocus
              required
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-name`}>Name (optional)</FieldLabel>
            <Input
              id={`${id}-name`}
              value={name}
              maxLength={200}
              placeholder="Brand critic"
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!what.trim() || !deId || send.isPending}>
              {send.isPending ? "Sending…" : "Send request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** A helper request's stage as a badge. */
export function HelperStageBadge({ stage }: { stage: HelperStage }) {
  const s = HELPER_STAGE[stage];
  return (
    <Badge variant={s.variant} data-stage={stage}>
      {s.label}
    </Badge>
  );
}

/**
 * A DE's helper requests and their stages, newest first; a quote shows its price and, for the
 * requester or a manager, Approve. Nothing when there are none.
 */
export function HelperRequestsList({
  api,
  company,
  deId,
  meId,
}: {
  api: Api;
  company: CompanyAccess;
  deId: string;
  meId: string | null;
}) {
  const qc = useQueryClient();
  const list = useQuery({
    ...helperRequestsQuery(api, company.id, deId),
    enabled: meId !== null || isManager(company),
    retry: false,
  });
  const approve = useMutation({
    mutationFn: (rid: string) =>
      api.client.agentRequests.approve({ params: { co: company.id, id: rid } }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: agentRequestKeys.list(company.id) });
      toast.add({ title: "Quote approved", type: "success" });
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "The quote was not approved."), type: "error" }),
  });
  const rows = list.data?.data ?? [];
  if (!rows.length) return null;
  return (
    <section className="flex flex-col gap-2" data-slot="helper-requests">
      <h3 className="m-0 flex items-center gap-1 text-xs font-medium text-muted-foreground">
        Requests
        <InfoTip label="About helper requests">
          Requested, then a quote; you approve it, it is paid, built and added.
        </InfoTip>
      </h3>
      <ul className="m-0 flex list-none flex-col divide-y divide-subtle rounded-lg border border-border p-0">
        {rows.map((r) => {
          const stage = r.stage ?? "requested";
          const mine = r.requested_by_membership_id === meId;
          return (
            <li
              key={r.id}
              data-helper-request={r.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm"
            >
              <span className="min-w-0 truncate font-medium">{r.role}</span>
              <span className="text-xs text-muted-foreground">
                {r.requested_by_name} · {formatDate(r.created_at.slice(0, 10))}
              </span>
              {r.note && r.note !== r.role ? (
                <InfoTip label="What it should do">{r.note}</InfoTip>
              ) : null}
              <span className="ml-auto flex items-center gap-2">
                {r.quote && stage !== "declined" ? (
                  <span className="text-xs tabular-nums" data-slot="helper-quote">
                    {quoteText(r.quote)}
                  </span>
                ) : null}
                {r.quote?.note ? <InfoTip label="Quote note">{r.quote.note}</InfoTip> : null}
                {r.resolution_note ? <InfoTip label="Note">{r.resolution_note}</InfoTip> : null}
                <HelperStageBadge stage={stage} />
                {stage === "quoted" && (mine || isManager(company)) ? (
                  <Button
                    size="xs"
                    data-slot="approve-quote"
                    disabled={approve.isPending}
                    onClick={() => approve.mutate(r.id)}
                  >
                    Approve
                  </Button>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
